import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Observable, of } from 'rxjs';
import moment from 'moment';
import { Storage } from '@ionic/storage';
import { catchError, exhaustMap, map, startWith, switchMap } from 'rxjs/operators';
import { AgendaHelperService } from './agenda.helper.service';
import { LocalStorage } from '../local-storage/local-storage';
import { ISynchronisationService } from '../synchronisation/iSynchronisation-service';
import { UserService } from '../auth/user.service';
import { NetworkProvider } from '../network/network';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { ByDayCollection } from '../../models/collections.model';
import { AgendaJourOt } from '../../models/agenda/agenda-jour.model';
import { environment } from '../../../../environments/environment';
import { OT } from '../../models/agenda/ot.model';

@Injectable({ providedIn: 'root' })
export class AgendaOtService extends LocalStorage implements ISynchronisationService {
  constructor(
    private userService: UserService,
    private http: HttpClient,
    protected override storage: Storage,
    protected override network: NetworkProvider,
    private agendaHelperService: AgendaHelperService
  ) {
    super();
    this.serverRequestPeriod = this.agendaHelperService.AGENDA_REFRESH_DELAY;
  }

  /**
   * Synchronisation forcé du service.
   * @returns Observable retournant le nombre d'entitées disponible en local après la synchronisation.
   */
  synchronisation(): Observable<number> {
    return this.getAgendaOtServer(moment().toDate()).pipe(
      switchMap(() => {
        return fromPromise(this.storage.get(this.AGENDA_OT_KEY)).pipe(
          switchMap((agenda) => {
            const localData = new ByDayCollection<AgendaJourOt>(agenda);
            let count = 0;

            for (const day of localData.getValues()) {
              for (const list in day) {
                count += day[list].length;
              }
            }

            return of(count);
          }),
          catchError(() => of(0))
        );
      })
    );
  }

  /**
   * Retourne les OT de l'agenda de l'utilisateur autour de la date, soit depuis le local storage,
   * soit depuis le serveur
   * @param displayedDate Date courante
   */
  getAgendaOt(displayedDate: Date, forceRefresh = false): Observable<ByDayCollection<AgendaJourOt>> {
    return interval(this.serverRequestPeriod).pipe(
      map((iteration) => !!iteration),
      startWith(forceRefresh),
      switchMap((forceServerRequest) => {
        return this.shouldUseWebservice(this.AGENDA_OT_KEY, forceServerRequest).pipe(
          exhaustMap((shouldUseWebservice) => {
            if (forceServerRequest && this.network.isConnected()) {
              return this.getAgendaOtServer(moment().toDate());
            } else if (shouldUseWebservice && this.network.isConnected()) {
              return this.getAgendaOtServer(displayedDate);
            } else {
              return fromPromise(this.storage.get(this.AGENDA_OT_KEY)).pipe(
                exhaustMap((agendaData: { [id: string]: AgendaJourOt }) => {
                  const agenda = new ByDayCollection(agendaData);
                  const displayedDateFormat = moment(displayedDate).format('YYYY-MM-DD');
                  if (agenda.has(displayedDateFormat) || !this.network.isConnected()) {
                    return of(agenda);
                  } else {
                    return this.getAgendaOtServer(displayedDate);
                  }
                })
              );
            }
          })
        );
      }),
      catchError(() => {
        return fromPromise(this.storage.get(this.AGENDA_OT_KEY)).pipe(
          map((agendaData: { [id: string]: AgendaJourOt }) => new ByDayCollection<AgendaJourOt>(agendaData)),
          catchError(() => of(new ByDayCollection<AgendaJourOt>()))
        );
      })
    );
  }

  /**
   * Récupère les OT de l'agenda sur le serveur sur une periode de 3 mois autour de la date passée en paramètre
   * @param displayedDate Date courante
   */
  private getAgendaOtServer(displayedDate: Date): Observable<ByDayCollection<AgendaJourOt>> {
    const days = this.network.isConnected() ? 45 : 7;
    const dateDebut = moment(displayedDate).subtract(days, 'day');
    const dateFin = moment(displayedDate).add(days, 'day');
    const url = `${
      environment.apis.API_AGENDA_OT
    }/${this.userService.getId()}/ot?dateDebut=${dateDebut.toISOString()}&dateFin=${dateFin.toISOString()}`;

    return this.http.get<OT[]>(url).pipe(
      map((result) => {
        const resultCollection: ByDayCollection<AgendaJourOt> = this.mapperResponse(result);
        this.agendaHelperService.setCollectionToLocal(resultCollection, this.AGENDA_OT_KEY, dateDebut, dateFin);
        return resultCollection;
      })
    );
  }

  private mapperResponse(data: OT[]): ByDayCollection<AgendaJourOt> {
    const agendaResponse: ByDayCollection<AgendaJourOt> = new ByDayCollection<AgendaJourOt>();
    data.forEach((activite) => {
      const date = moment(activite.dateDebut).format('YYYY-MM-DD');

      let agenda: AgendaJourOt = { ots: [] };
      if (agendaResponse.has(date)) {
        agenda = agendaResponse.get(date);
      }

      agenda.ots.push(activite);
      agendaResponse.put(date, agenda);
    });
    return agendaResponse;
  }
}
